import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { customerApi } from '../services/userAuth';
import { Button } from '../components/ui/button';
import { Loader2, ArrowLeft, Save, X, AlertTriangle, CheckCircle2, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pending: { label: 'Pending Payment', color: 'text-amber-700 bg-amber-50 border-amber-200', icon: Clock },
  paid: { label: 'Confirmed', color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  cancellation_requested: { label: 'Cancellation Requested', color: 'text-orange-700 bg-orange-50 border-orange-200', icon: Clock },
  cancelled: { label: 'Cancelled', color: 'text-slate-700 bg-slate-100 border-slate-200', icon: X },
  completed: { label: 'Completed', color: 'text-blue-700 bg-blue-50 border-blue-200', icon: CheckCircle2 },
};

function uiStatus(r) {
  if (['cancelled', 'cancellation_requested', 'completed'].includes(r.status)) return r.status;
  if (r.payment_status === 'paid' || r.status === 'paid' || r.status === 'confirmed') return 'paid';
  return 'pending';
}

export default function MyReservation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    customerApi.myReservation(id)
      .then(async (data) => {
        setR(data);
        setForm({
          phone: data.phone,
          pickup_date: data.pickup_date,
          pickup_location: data.pickup_location,
          pickup_address: data.pickup_address || '',
          pickup_apartment: data.pickup_apartment || '',
          pickup_city: data.pickup_city || '',
          pickup_zip: data.pickup_zip || '',
          delivery_date: data.delivery_date,
          delivery_location: data.delivery_location,
          delivery_address: data.delivery_address || '',
          delivery_apartment: data.delivery_apartment || '',
          delivery_city: data.delivery_city || '',
          delivery_zip: data.delivery_zip || '',
        });
        try {
          const s = await customerApi.getSchool(data.school_id);
          setSchool(s);
        } catch { /* ignore */ }
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || 'Failed to load reservation');
        navigate('/account', { replace: true });
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-6 h-6 text-[#1a2e4e] animate-spin" /></div>;
  }
  if (!r) return null;

  const status = uiStatus(r);
  const statusInfo = STATUS_LABEL[status] || STATUS_LABEL.pending;
  const StatusIcon = statusInfo.icon;
  const locked = ['cancelled', 'completed'].includes(r.status);

  const saveEdits = async () => {
    setSaving(true);
    try {
      // Recompute pickup/delivery surcharges if dates changed
      const body = { ...form };
      if (school) {
        const pkSlot = school.pickup_dates.find(d => d.date === form.pickup_date);
        const delSlot = school.delivery_dates.find(d => d.date === form.delivery_date);
        if (pkSlot) body.pickup_date_surcharge = pkSlot.surcharge;
        if (delSlot) body.delivery_date_surcharge = delSlot.surcharge;
      }
      const updated = await customerApi.updateReservation(id, body);
      setR(updated);
      setEdit(false);
      toast.success('Reservation updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  const requestCancel = async () => {
    if (!window.confirm('Request to cancel this reservation? Our team will follow up about your $30 deposit.')) return;
    setCancelling(true);
    try {
      const updated = await customerApi.requestCancel(id);
      setR(updated);
      toast.success('Cancellation requested. We will email you shortly.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Cancellation failed');
    } finally {
      setCancelling(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="bg-gradient-to-b from-white to-slate-50 min-h-[calc(100vh-200px)]" data-testid="my-reservation-page">
      <div className="max-w-3xl mx-auto px-4 py-10">
        <Link to="/account" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#1a2e4e] mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to my reservations
        </Link>

        <div className="flex items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#1a2e4e]" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {r.school_name}
            </h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className="font-mono text-xs text-slate-500">{r.reference_number}</span>
              <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${statusInfo.color}`} data-testid="reservation-status-badge">
                <StatusIcon className="w-3 h-3" /> {statusInfo.label}
              </span>
            </div>
          </div>
          {!locked && (
            edit ? (
              <Button variant="ghost" onClick={() => setEdit(false)} data-testid="cancel-edit-btn">
                <X className="w-4 h-4 mr-1" /> Cancel
              </Button>
            ) : (
              <Button onClick={() => setEdit(true)} className="bg-[#1a2e4e] hover:bg-[#162641] text-white" data-testid="edit-reservation-btn">
                Edit Details
              </Button>
            )
          )}
        </div>

        {r.status === 'cancellation_requested' && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 text-sm text-orange-800 mb-5 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>You've requested to cancel. Our team will reach out shortly about your $30 deposit refund.</span>
          </div>
        )}

        <div className="space-y-5">
          {/* Contact */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="section-contact">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Contact</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <Field label="Name" value={`${r.first_name} ${r.last_name}`} />
              <Field label="Email" value={r.email} />
              {edit ? (
                <EditField label="Phone" value={form.phone} onChange={upd('phone')} testid="edit-phone" />
              ) : (
                <Field label="Phone" value={r.phone} />
              )}
            </div>
          </section>

          {/* Pickup */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="section-pickup">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Pickup</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {edit && school ? (
                <>
                  <SelectField label="Pickup Date" value={form.pickup_date} onChange={upd('pickup_date')} testid="edit-pickup-date"
                    options={school.pickup_dates.map(d => ({
                      value: d.date,
                      label: `${d.date}${d.surcharge ? ` (+$${d.surcharge})` : ''}`,
                    }))} />
                  <SelectField label="Pickup Location" value={form.pickup_location} onChange={upd('pickup_location')} testid="edit-pickup-location"
                    options={school.dorms.map(d => ({ value: d, label: d }))} />
                </>
              ) : (
                <>
                  <Field label="Date" value={r.pickup_date + (r.pickup_date_surcharge ? ` (+$${r.pickup_date_surcharge})` : '')} />
                  <Field label="Location" value={r.pickup_location} />
                </>
              )}
              {(edit && form.pickup_location === 'Off-Campus') || (!edit && r.pickup_location === 'Off-Campus') ? (
                <>
                  {edit ? (
                    <>
                      <EditField label="Address" value={form.pickup_address} onChange={upd('pickup_address')} testid="edit-pickup-address" />
                      <EditField label="Apt / Unit" value={form.pickup_apartment} onChange={upd('pickup_apartment')} />
                      <EditField label="City" value={form.pickup_city} onChange={upd('pickup_city')} />
                      <EditField label="ZIP" value={form.pickup_zip} onChange={upd('pickup_zip')} />
                    </>
                  ) : (
                    <>
                      <Field label="Address" value={r.pickup_address} />
                      <Field label="Apt / Unit" value={r.pickup_apartment} />
                      <Field label="City" value={r.pickup_city} />
                      <Field label="ZIP" value={r.pickup_zip} />
                    </>
                  )}
                </>
              ) : null}
            </div>
          </section>

          {/* Delivery */}
          <section className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="section-delivery">
            <h2 className="text-sm font-semibold text-slate-700 mb-3">Delivery</h2>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              {edit && school ? (
                <>
                  <SelectField label="Delivery Date" value={form.delivery_date} onChange={upd('delivery_date')} testid="edit-delivery-date"
                    options={school.delivery_dates.map(d => ({
                      value: d.date,
                      label: `${d.date}${d.surcharge ? ` (+$${d.surcharge})` : ''}`,
                    }))} />
                  <SelectField label="Delivery Location" value={form.delivery_location} onChange={upd('delivery_location')} testid="edit-delivery-location"
                    options={school.dorms.map(d => ({ value: d, label: d }))} />
                </>
              ) : (
                <>
                  <Field label="Date" value={r.delivery_date + (r.delivery_date_surcharge ? ` (+$${r.delivery_date_surcharge})` : '')} />
                  <Field label="Location" value={r.delivery_location} />
                </>
              )}
              {(edit && form.delivery_location === 'Off-Campus') || (!edit && r.delivery_location === 'Off-Campus') ? (
                <>
                  {edit ? (
                    <>
                      <EditField label="Address" value={form.delivery_address} onChange={upd('delivery_address')} testid="edit-delivery-address" />
                      <EditField label="Apt / Unit" value={form.delivery_apartment} onChange={upd('delivery_apartment')} />
                      <EditField label="City" value={form.delivery_city} onChange={upd('delivery_city')} />
                      <EditField label="ZIP" value={form.delivery_zip} onChange={upd('delivery_zip')} />
                    </>
                  ) : (
                    <>
                      <Field label="Address" value={r.delivery_address} />
                      <Field label="Apt / Unit" value={r.delivery_apartment} />
                      <Field label="City" value={r.delivery_city} />
                      <Field label="ZIP" value={r.delivery_zip} />
                    </>
                  )}
                </>
              ) : null}
            </div>
          </section>

          {/* Save / Cancel actions */}
          {edit && (
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEdit(false)}>Cancel</Button>
              <Button onClick={saveEdits} disabled={saving} className="bg-[#dc2626] hover:bg-[#b91c1c] text-white" data-testid="save-reservation-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
                Save Changes
              </Button>
            </div>
          )}

          {/* Cancellation request */}
          {!locked && r.status !== 'cancellation_requested' && !edit && (
            <div className="border-t border-slate-200 pt-5">
              <button onClick={requestCancel} disabled={cancelling} data-testid="request-cancel-btn"
                className="text-sm text-red-600 hover:underline disabled:opacity-50">
                {cancelling ? 'Submitting…' : 'Request to cancel this reservation'}
              </button>
              <p className="text-xs text-slate-400 mt-1">We'll review and reach out about your $30 deposit refund.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value || '—'}</p>
    </div>
  );
}

function EditField({ label, value, onChange, testid }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <input value={value || ''} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20" />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <p className="text-xs text-slate-500 mb-1">{label}</p>
      <select value={value || ''} onChange={onChange} data-testid={testid}
        className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm outline-none focus:ring-2 focus:ring-[#1a2e4e]/20 bg-white">
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
